import { LoadState } from "../../constants/enums";

export type LogExecuteOn = "pending" | "fulfilled" | "rejected";

export interface ILogEntry {
  module_name: string;
  sub_module_name: string;
  action_name: 'page_load' |
  'button_click' | 'approval' | 'download' | 'form_submit' | 'tab_switch';
  request_payload?: Record<string, any>;
}

/**
 * Internal log entry used by thunks that dispatch through the middleware.
 * The `executeOn` property controls which createAsyncThunk lifecycle
 * action(s) trigger the log POST. This property is stripped before
 * sending to the server and is NOT exposed to manual `logAccess` callers.
 */
export interface IInternalLogEntry extends ILogEntry {
  executeOn: LogExecuteOn[];
}

export interface IUserTimelineEntry {
  access_log_code: string;
  access_log_id: number;
  action_name: string;
  branch_code: string;
  endpoint: string;
  event_date: string;
  event_hour: string;
  event_ts: string;
  event_type: string;
  http_method: string;
  ip_address: string;
  module_name: string;
  response_time_ms: string;
  status_code: string;
  sub_module_name: string;
  user_email: string;
  user_fact_id: number;
  user_full_name: string;
  user_role_name: string;
}

export interface IModuleUsageEntry {
  activity_date: string;
avg_response_ms: string;
error_count: string;
event_type: string;
max_response_ms: string;
module_name: string;
sub_module_name: string;
total_hits: number;
unique_users: number;
}

export interface ITopUserEntry {
  active_days: number;
  activity_date: string;

  api_calls: number;
  avg_response_ms: number | null;

  branch_code: string | null;

  first_event_ts: string;
  last_event_ts: string; 

  total_events: number;
  ui_events: number;

  user_email: string;
  user_fact_id: number;
  user_full_name: string;
  user_role_name: string; 
}

export interface ISlowApiEntry {
  endpoint_path: string;
  avg_response_time: number;
  max_response_time: number;
  request_count: number;
}

export interface IErrorRateEntry {
  endpoint_path: string;
  total_requests: number;
  error_count: number;
  error_percentage: number;
  last_error_ts: string;
}

export interface IAccessorRoleSnapshot {
  email: string;
  role_id: number;
  full_name: string;
  last_name: string;
  role_name: string;
  first_name: string;
  user_fact_id: number;
}

export interface IBankingAccessLogEntry {
  access_type: string;
  target_id: string;
  table_name: string | null;
  accessed_by_user_fact_id: number;
  accessor_role_snapshot: IAccessorRoleSnapshot;
  access_reason: string;
  ip_address: string;
  user_agent: string;
  endpoint: string;
  status: string;
  is_self_access: number;
  create_ts: string;
}

interface IListState<T> {
  list: T[];
  loading: LoadState;
  totalRecords: number;
  error: string | null;
}

export interface IAccessLogsState {
  userTimeline: IListState<IUserTimelineEntry>;
  moduleUsage: IListState<IModuleUsageEntry>;
  topUsers: IListState<ITopUserEntry>;
  slowApis: IListState<ISlowApiEntry>;
  errorRate: IListState<IErrorRateEntry>;
  bankingLogs: IListState<IBankingAccessLogEntry>;
}

export const defaultListState = <T>(): IListState<T> => ({
  list: [],
  loading: LoadState.NotLoaded,
  totalRecords: 0,
  error: null,
});

export const initialAccessLogsState: IAccessLogsState = {
  userTimeline: defaultListState<IUserTimelineEntry>(),
  moduleUsage: defaultListState<IModuleUsageEntry>(),
  topUsers: defaultListState<ITopUserEntry>(),
  slowApis: defaultListState<ISlowApiEntry>(),
  errorRate: defaultListState<IErrorRateEntry>(),
  bankingLogs: defaultListState<IBankingAccessLogEntry>(),
};
